import {
    ADD_CUSTOM_TOAST,
    ADD_MULTIPLE_TOASTS,
    ADD_TOAST,
    REMOVE_TOAST,
    REMOVE_TOASTS
} from '@/utils/hooks/context/toast/actions';

import { ERROR_SECONDARY, GREEN, PRIMARY } from '@/components/base/Button';

export type NotificationIdType = string;

enum collaborationsTypes {
    POST_STATUS_UPDATED = 'collaboration_post_status_updated',
    POST_NOTE_CREATED = 'collaboration_post_note_created'
}

enum contentTypes {
    NEW_CONTENT_AUTOMATIC_ALBUM = 'new_content_automatic_album'
}

enum conversationsTypes {
    COMMENT_PICKER_WINNER_CONFIRMATION = 'comment_picker_winner_confirmation'
}

enum dashboardTypes {
    INVITATION_ACCEPTED = 'invitation_accepted',
    INVITATION_RECEIVED = 'invitation_received'
}

enum usersAndRolesTypes {
    CONFIRM_INVITATION_ACCEPTED = 'confirm_invitation_accepted',
    CONFIRM_INVITATION_BY_MANAGER_ACCEPTED = 'confirm_invitation_by_manager_accepted'
}

enum analyticsTypes {
    FIRST_STATS_READY = 'first_stats_ready'
}

enum reportsTypes {
    PENDING_REPORTS_READY = 'pending_report_ready'
}

enum subscriptionTypes {
    DOWNGRADE_BEING_EFFECTIVE = 'downgrade_being_effective',
    DOWNGRADE_REMINDER = 'downgrade_reminder',
    DOWNGRADE_REMINDER_ADMIN = 'downgrade_reminder_admin',
    OPTIONS_CANCELLATION_BEING_EFFECTIVE = 'options_cancellation_being_effective',
    OPTIONS_CANCELLATION_REMINDER = 'options_cancellation_reminder',
    PLAN_CANCELLATION_REMINDER = 'plan_cancellation_reminder',
    PLAN_CANCELLATION_REMINDER_ADMIN = 'plan_cancellation_reminder_admin',
    RENEW_REMINDER_MANUAL_PLANS = 'renew_reminder_manual_plans'
}

export enum schedulerTypes {
    POST_PUBLISHED_FAILED = 'post_publish_failed',
    POST_UPDATED = 'scheduler_post_updated',
    POST_APPROVED_INFO = 'post_approved_info',
    POST_APPROVED_ACTION_REQUIRED = 'post_approved_action_required',
    POST_REJECTED_INFO = 'post_rejected_info',
    POST_REJECTED_ACTION_REQUIRED = 'post_rejected_action_required',
    MULTIPOST_APPROVED_INFO = 'multipost_approved_info',
    MULTIPOST_APPROVED_ACTION_REQUIRED = 'multipost_approved_action_required'
}

export type NotificationVariant =
    | typeof PRIMARY
    | typeof GREEN
    | typeof ERROR_SECONDARY
    | 'gray_100';

export type NotificationOptionsType = {
    progressBar?: {
        show: boolean;
        duration?: number;
        pause?: boolean;
        onAnimationEnd?: () => void;
    };
    wide?: boolean;
    hideContent?: boolean;
    hideToast?: () => void;
    cancelAction?: () => void;
    variant?: NotificationVariant;
};

export type NotificationDefaultTypes = {
    id: NotificationIdType;
    data: Record<string, any>;
    read: boolean;
    created_at: string;
    options?: NotificationOptionsType;
};

export type NotificationCollaborationType = NotificationDefaultTypes & {
    category: 'collaboration';
    type: collaborationsTypes;
};

export type NotificationContentType = NotificationDefaultTypes & {
    category: 'content';
    type: contentTypes;
};

export type NotificationConversationsType = NotificationDefaultTypes & {
    category: 'conversations';
    type: conversationsTypes;
};

export type NotificationUsersAndRolesType = NotificationDefaultTypes & {
    category: 'users_and_roles';
    type: usersAndRolesTypes;
};

export type NotificationAnalyticsType = NotificationDefaultTypes & {
    category: 'analytics';
    type: analyticsTypes;
};

export type NotificationReportsType = NotificationDefaultTypes & {
    category: 'reports';
    type: reportsTypes;
};

export type NotificationSubscriptionType = NotificationDefaultTypes & {
    category: 'subscription';
    type: subscriptionTypes;
};

export type NotificationDashboardType = NotificationDefaultTypes & {
    category: 'dashboard';
    type: dashboardTypes;
};

export type NotificationSchedulerType = NotificationDefaultTypes & {
    category: 'scheduler';
    type: schedulerTypes;
};

export type NotificationType =
    | NotificationAnalyticsType
    | NotificationCollaborationType
    | NotificationContentType
    | NotificationConversationsType
    | NotificationDashboardType
    | NotificationReportsType
    | NotificationSubscriptionType
    | NotificationUsersAndRolesType
    | NotificationSchedulerType;

export type CustomNotificationDataType = {
    title: React.ReactNode;
    description?: React.ReactNode;
    icon?: string;
    action?: React.ReactNode;
};

export type CustomNotificationType = {
    id: NotificationIdType;
    category: 'custom';
    data: CustomNotificationDataType;
    options?: NotificationOptionsType;
};

export type AllNotificationsType = CustomNotificationType | NotificationType;

export type ToastsReducerActionsType =
    | {
          type: typeof ADD_TOAST;
          payload: NotificationType;
      }
    | {
          type: typeof ADD_MULTIPLE_TOASTS;
          payload: NotificationType[];
      }
    | {
          type: typeof ADD_CUSTOM_TOAST;
          payload: Pick<CustomNotificationType, 'data' | 'options' | 'id'>;
      }
    | {
          type: typeof REMOVE_TOAST;
          payload: NotificationIdType;
      }
    | {
          type: typeof REMOVE_TOASTS;
      };

export type AllNotificationsContentProps = {
    notification: CustomNotificationType | NotificationType;
};

export type NotificationCustomProps = {
    notification: CustomNotificationType;
};

export type NotificationAnalyticsProps = {
    notification: NotificationAnalyticsType;
};

export type NotificationCollaborationProps = {
    notification: NotificationCollaborationType;
};

export type NotificationContentProps = {
    notification: NotificationContentType;
};

export type NotificationConversationsProps = {
    notification: NotificationConversationsType;
};

export type NotificationDashboardProps = {
    notification: NotificationDashboardType;
};

export type NotificationReportsProps = {
    notification: NotificationReportsType;
};

export type NotificationSubscriptionProps = {
    notification: NotificationSubscriptionType;
};

export type NotificationUsersAndRolesProps = {
    notification: NotificationUsersAndRolesType;
};

export type NotificationSchedulerProps = {
    notification: NotificationSchedulerType;
};
